import React, { useEffect, useState } from 'react'
import { Popover, Upload } from 'antd'
import { ApiRequest } from '../../../services/api/api'
import moment from 'moment'

const STORED_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
const STORED_DATETIME_FORMATS_NO_OFFSET = ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm']

const toLocalMoment = (value: any) => {
  if (!value) return null
  const parsed = moment.utc(value, [STORED_DATETIME_FORMAT, ...STORED_DATETIME_FORMATS_NO_OFFSET], true)
  return parsed.isValid() ? parsed.local() : null
}

let cachedEmployeeMap: Record<number, string> = {}
let employeeFetchPromise: Promise<void> | null = null

const loadEmployees = (): Promise<void> => {
  if (Object.keys(cachedEmployeeMap).length) return Promise.resolve()
  if (!employeeFetchPromise) {
    employeeFetchPromise = ApiRequest({ url: 'Employee/GetAllEmployee', method: 'get' }).then((res: any) => {
      const map: Record<number, string> = {}
      ;(res?.data?.data || []).forEach((e: any) => {
        map[e.employeeId] = `${e.firstName}  ${e.lastName}`
      })
      cachedEmployeeMap = map
    })
  }
  return employeeFetchPromise!
}

const EmployeeName = ({ id }: { id: number | null | undefined }) => {
  const [name, setName] = useState<string>(() => (id ? cachedEmployeeMap[id] || '' : ''))

  useEffect(() => {
    if (!id) return
    if (cachedEmployeeMap[id]) {
      setName(cachedEmployeeMap[id])
      return
    }
    loadEmployees().then(() => setName(cachedEmployeeMap[id] || ''))
  }, [id])

  return <>{name}</>
}

let cachedMachineNameMap: Record<string, string> = {}
let machineFetchPromise: Promise<void> | null = null

const loadMachines = (): Promise<void> => {
  if (Object.keys(cachedMachineNameMap).length) return Promise.resolve()
  if (!machineFetchPromise) {
    machineFetchPromise = ApiRequest({ url: 'Machine/GetAllMachine', method: 'get' }).then((res: any) => {
      const map: Record<string, string> = {}
      ;(res?.data?.data || []).forEach((m: any) => {
        map[m.machineId] = m.machineName
      })
      cachedMachineNameMap = map
    })
  }
  return machineFetchPromise!
}

const MachineName = ({ id }: { id: string | null | undefined }) => {
  const [name, setName] = useState<string>(() => (id ? cachedMachineNameMap[id] || '' : ''))

  useEffect(() => {
    if (!id) return
    if (cachedMachineNameMap[id]) {
      setName(cachedMachineNameMap[id])
      return
    }
    loadMachines().then(() => setName(cachedMachineNameMap[id] || id))
  }, [id])

  return <>{name}</>
}

const getImgs = (imgStr: string) => {
  if (!imgStr) return []
  try {
    const parsed = JSON.parse(imgStr)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return []
  }
}
const getBeforeImgs = (imgStr: string) => getImgs(imgStr).filter((img: any) => img.stage !== 'after')
const getAfterImgs = (imgStr: string) => getImgs(imgStr).filter((img: any) => img.stage === 'after')

const ProductionMaintenanceLogColumnModel = (): any => {
  return [
    {
      title: 'Date',
      field: 'date',
      editable: 'never',
      render: (rowData: any) => rowData.date ? moment(rowData.date).format('YYYY-MM-DD HH:mm') : '',
    },
    {
      title: 'Machine Name',
      field: 'machineId',
      render: (rowData: any) => <MachineName id={rowData.machineId} />,
    },
    {
      title: 'Problem Description',
      field: 'problemDescription',
    },
    {
      title: 'Maintenance Description',
      field: 'maintenanceDescription',
    },
    {
      title: 'Maintenance Type',
      field: 'maintenanceType',
    },
    {
      title: 'Is it Intrusive Maintenance',
      field: 'isIntrusiveMaintenance',
      render: (rowData: any) => rowData.isIntrusiveMaintenance === null || rowData.isIntrusiveMaintenance === undefined ? '' : rowData.isIntrusiveMaintenance ? 'Yes' : 'No',
    },
    {
      title: 'Time Start',
      field: 'timeStart',
      render: (rowData: any) => {
        const parsed = toLocalMoment(rowData.timeStart)
        return parsed ? <span style={{ whiteSpace: 'nowrap' }}>{parsed.format('YYYY-MM-DD HH:mm')}</span> : ''
      },
    },
    {
      title: 'Time Finish',
      field: 'timeFinish',
      render: (rowData: any) => {
        const parsed = toLocalMoment(rowData.timeFinish)
        return parsed ? <span style={{ whiteSpace: 'nowrap' }}>{parsed.format('YYYY-MM-DD HH:mm')}</span> : ''
      },
    },
    {
      title: 'Parts replacement detail, if any',
      field: 'replacementDetail',
    },
    {
      title: 'Post-Maintenance Clean Done?',
      field: 'postMaintenanceClean',
      render: (rowData: any) => rowData.postMaintenanceClean === null || rowData.postMaintenanceClean === undefined ? '' : rowData.postMaintenanceClean ? 'Yes' : 'No',
    },
    {
      title: 'Maintenance Done by',
      field: 'maintenanceDoneById',
      render: (rowData: any) => <EmployeeName id={rowData.maintenanceDoneById} />,
    },
    {
      title: 'Production Verification by',
      field: 'productionVerificationById',
      render: (rowData: any) => <EmployeeName id={rowData.productionVerificationById} />,
    },
    {
	    title: 'Maintain Before',
	    field: 'images_before',
	    render: (rowData: any) => getBeforeImgs(rowData.images).length ?
			(<Popover content = {
				<div style={{width: '66rem', display: 'flex', flexWrap: 'wrap'}}>
					{getBeforeImgs(rowData.images).map((img: any) => (
						<div key={img.uid} style={{width: '20rem', height: '20rem', margin: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
							<Upload className="img-preview"
								listType="picture-card"
								fileList={[img]}
								disabled>
							</Upload>
						</div>
					))}
				</div>
			} title="Photo Details">
				<div style={{width: '60px', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
					<img style={{width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%'}} src={getBeforeImgs(rowData.images)[0]?.url ?? null} />
				</div>
			</Popover>) : null
	  },
    {
	    title: 'Maintain After',
	    field: 'images_after',
	    render: (rowData: any) => getAfterImgs(rowData.images).length ?
			(<Popover content = {
				<div style={{width: '66rem', display: 'flex', flexWrap: 'wrap'}}>
					{getAfterImgs(rowData.images).map((img: any) => (
						<div key={img.uid} style={{width: '20rem', height: '20rem', margin: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
							<Upload className="img-preview"
								listType="picture-card"
								fileList={[img]}
								disabled>
							</Upload>
						</div>
					))}
				</div>
			} title="Photo Details">
				<div style={{width: '60px', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
					<img style={{width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%'}} src={getAfterImgs(rowData.images)[0]?.url ?? null} />
				</div>
			</Popover>) : null
	  }
  ]
}


export default ProductionMaintenanceLogColumnModel
